import React from "react";
import { CheckCircle2, LockKeyhole, ShieldCheck, Sparkles } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Switch,
} from "@exiftrator/shared/components/ui";
import { useLicense, useFeatureAccess } from "../../../contexts/LicenseContext";

function ToggleRow({
  title,
  description,
  checked,
  onCheckedChange,
  disabled = false,
  ariaLabel,
}) {
  return (
    <div className={`flex items-center justify-between gap-4 rounded-lg border border-border-subtle bg-bg-primary/40 p-4 ${disabled ? "opacity-70" : ""}`}>
      <div>
        <p className="text-sm font-semibold text-text-primary">{title}</p>
        <p className="text-xs text-text-secondary">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />
    </div>
  );
}

function FeatureRow({ label, allowed }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-bg-primary/40 px-3 py-2">
      <div className="flex items-center gap-2">
        <LockKeyhole className="size-3.5 text-text-secondary" />
        <span className="text-sm text-text-primary">{label}</span>
      </div>
      <Badge variant={allowed ? "success" : "secondary"} size="sm">
        {allowed ? "Enabled" : "Locked"}
      </Badge>
    </div>
  );
}

function LicenseSettings() {
  const {
    tier,
    gatingEnabled,
    proUnlocked,
    bypassEnabled,
    allowBypass,
    isLoading,
    setGatingEnabled,
    setProUnlocked,
    setBypassEnabled,
  } = useLicense();

  const presetsAccess = useFeatureAccess("presets");
  const fieldAccess = useFeatureAccess("fieldCustomization");
  const adFreeAccess = useFeatureAccess("adFreeMode");

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-sans text-[24px] font-bold text-text-primary">
          License & Access
        </h2>
        <Badge variant={tier === "pro" ? "accent" : "secondary"}>
          {tier === "pro" ? "PRO" : "FREE"}
        </Badge>
      </div>

      <div className="flex-1 overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-white/10 pb-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <Alert>
          <Sparkles className="size-4 text-accent-primary" />
          <AlertTitle>Phase 1 Licensing Scaffold</AlertTitle>
          <AlertDescription>
            Local switches below control access gating on this device. Payment/license
            verification is intentionally deferred to the next phase.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="size-4 text-accent-primary" />
              Access Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ToggleRow
              title="Enable Pro Feature Gating"
              description="When off, all pro-gated features remain available regardless of tier."
              checked={gatingEnabled}
              onCheckedChange={setGatingEnabled}
              disabled={isLoading}
              ariaLabel="Enable Pro Feature Gating"
            />

            <ToggleRow
              title="Pro Unlocked"
              description="Local Pro entitlement used by this phase's scaffold."
              checked={proUnlocked}
              onCheckedChange={setProUnlocked}
              disabled={isLoading}
              ariaLabel="Pro Unlocked"
            />

            {allowBypass && (
              <ToggleRow
                title="Bypass Gating (Dev only)"
                description="Development-only bypass that forces gated features to unlock."
                checked={bypassEnabled}
                onCheckedChange={setBypassEnabled}
                disabled={isLoading}
                ariaLabel="Bypass Gating"
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="size-4 text-accent-primary" />
              Pro Feature Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <FeatureRow label="Presets" allowed={presetsAccess.allowed} />
            <FeatureRow label="Field list customization" allowed={fieldAccess.allowed} />
            <FeatureRow label="Ads-free mode (coming soon)" allowed={adFreeAccess.allowed} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default LicenseSettings;
